import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  addDoc,
  updateDocument,
  deleteDocument,
  orderBy,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import type {
  ProjectBoardDocument,
  ProjectBoardColumn,
  ProjectTaskDocument,
  ProjectBoardColumnId,
} from '@/types'
import {
  createProjectBoardFromEngagementFn,
  createChildProjectBoardFn,
} from '@/lib/firebase/functions'

const tasksCol = (boardId: string) =>
  typedCollection<ProjectTaskDocument>(`projectBoards/${boardId}/tasks`)

export const projectBoardsService = {
  async getBoard(boardId: string): Promise<ProjectBoardDocument | null> {
    return getDocument(typedDoc<ProjectBoardDocument>('projectBoards', boardId))
  },

  async listTasks(boardId: string): Promise<Array<ProjectTaskDocument & { id: string }>> {
    return getCollection(tasksCol(boardId), orderBy('order', 'asc'))
  },

  async createTask(
    boardId: string,
    input: {
      title: string
      columnId: ProjectBoardColumnId
      order: number
      createdBy: string
      description?: string
      assigneeIds?: string[]
      dueDate?: string
      coverMediaId?: string
      coverMediaUrl?: string
      attachments?: Array<{ mediaId: string; url: string; name: string; mimeType: string }>
      priority?: 'low' | 'medium' | 'high'
      labelIds?: string[]
    }
  ): Promise<string> {
    const payload: Record<string, unknown> = {
      boardId,
      columnId: input.columnId,
      order: input.order,
      title: input.title,
      createdBy: input.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    if (input.description) payload.description = input.description
    if (input.assigneeIds && input.assigneeIds.length > 0) payload.assigneeIds = input.assigneeIds
    if (input.dueDate) payload.dueDate = input.dueDate
    if (input.coverMediaId) payload.coverMediaId = input.coverMediaId
    if (input.coverMediaUrl) payload.coverMediaUrl = input.coverMediaUrl
    if (input.attachments && input.attachments.length > 0) payload.attachments = input.attachments
    if (input.priority) payload.priority = input.priority
    if (input.labelIds && input.labelIds.length > 0) payload.labelIds = input.labelIds
    const ref = await addDoc(tasksCol(boardId), payload as unknown as ProjectTaskDocument)
    return ref.id
  },

  async updateTask(
    boardId: string,
    taskId: string,
    patch: Partial<
      Pick<
        ProjectTaskDocument,
        | 'title'
        | 'description'
        | 'columnId'
        | 'order'
        | 'assigneeIds'
        | 'dueDate'
        | 'coverMediaId'
        | 'coverMediaUrl'
        | 'attachments'
        | 'priority'
        | 'labelIds'
      >
    >
  ): Promise<void> {
    const ref = typedDoc<ProjectTaskDocument>(`projectBoards/${boardId}/tasks`, taskId)
    await updateDocument(ref, {
      ...patch,
      updatedAt: serverTimestamp(),
    } as unknown as ProjectTaskDocument)
  },

  async updateBoardColumns(boardId: string, columns: ProjectBoardColumn[]): Promise<void> {
    const ref = typedDoc<ProjectBoardDocument>('projectBoards', boardId)
    await updateDocument(ref, {
      columns,
      updatedAt: serverTimestamp(),
    } as unknown as ProjectBoardDocument)
  },

  async updateBoardMeta(
    boardId: string,
    meta: NonNullable<ProjectBoardDocument['meta']>
  ): Promise<void> {
    const ref = typedDoc<ProjectBoardDocument>('projectBoards', boardId)
    await updateDocument(ref, {
      meta,
      updatedAt: serverTimestamp(),
    } as unknown as ProjectBoardDocument)
  },

  async deleteTask(boardId: string, taskId: string): Promise<void> {
    await deleteDocument(
      typedDoc<ProjectTaskDocument>(`projectBoards/${boardId}/tasks`, taskId)
    )
  },

  async createRootBoard(
    engagementId: string,
    templateOverrides?: Record<string, string>
  ): Promise<string> {
    const { data } = await createProjectBoardFromEngagementFn({
      engagementId,
      ...(templateOverrides ? { templateOverrides } : {}),
    })
    return data.boardId
  },

  async createChildBoard(parentBoardId: string, parentTaskId: string): Promise<string> {
    const { data } = await createChildProjectBoardFn({ parentBoardId, parentTaskId })
    return data.boardId
  },
}
